import React, { useEffect, useLayoutEffect, useRef } from "react";

interface TextInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  /** Binds a `<label htmlFor>` to this control. */
  id?: string;
  multiline?: boolean;
  minRows?: number;
  type?: "text" | "password" | "number" | "email";
  autoComplete?: string;
  min?: number;
  max?: number;
  maxLength?: number;
  autoFocus?: boolean;
  ariaLabel?: string;
  /// Marks the field as failing validation — draws the error border and
  /// tells assistive tech, so the message beside it is not the only signal.
  ariaInvalid?: boolean;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

const autoResize = (el: HTMLTextAreaElement) => {
  el.style.height = "auto";
  el.style.height = `${el.scrollHeight}px`;
};

export const TextInput: React.FC<TextInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled,
  className = "input-custom-sm",
  name,
  id,
  multiline = false,
  minRows = 1,
  type = "text",
  autoComplete,
  min,
  max,
  maxLength,
  autoFocus,
  ariaLabel,
  ariaInvalid,
  onFocus,
  onBlur,
  onKeyDown,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // `html` and `body` never scroll here, but a plain focus() scrolls them
  // anyway to reveal the field — sliding the page out from under fixed menus.
  useEffect(() => {
    if (autoFocus) inputRef.current?.focus({ preventScroll: true });
  }, [autoFocus]);

  useLayoutEffect(() => {
    if (multiline && textareaRef.current) autoResize(textareaRef.current);
  }, [multiline, value]);

  useEffect(() => {
    if (!multiline || !textareaRef.current) return;
    const el = textareaRef.current;
    const onResize = () => autoResize(el);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [multiline]);

  if (multiline) {
    return (
      <textarea
        ref={textareaRef}
        name={name}
        id={id}
        aria-label={ariaLabel}
        className={`${className} h-auto! min-h-8! py-1.5! block! leading-5 w-full resize-none overflow-y-auto max-h-40`}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        maxLength={maxLength}
        rows={minRows}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      name={name}
      id={id}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid || undefined}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      autoComplete={autoComplete}
      min={min}
      max={max}
      maxLength={maxLength}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
    />
  );
};
